import { hero } from "../app.ts";
import { sfx } from "../audio.ts";
import { CHAPTERS, opponentById, type Line } from "../data/story.ts";
import { now, setScene } from "../engine.ts";
import { Actor } from "../render/actor.ts";
import { drawArena, GROUND, W } from "../render/arena.ts";
import type { Backdrop } from "../types.ts";
import { btn, h, mount } from "../ui/dom.ts";
import { muteButton } from "./common.ts";

interface Opts {
  lines: Line[];
  backdrop: Backdrop;
  /** Show a chapter title card first. */
  chapter?: number;
  onDone: () => void;
}

const CPS = 55;

export function cutsceneScreen({ lines, backdrop, chapter, onDone }: Opts): void {
  const me = hero();
  const heroActor = new Actor(me.look, me.gear, 1, 330);
  let other: Actor | null = null;
  let otherId = "";
  let index = -1;
  let shownAt = 0;
  let full = "";
  let done = false;

  setScene((ctx, t) => {
    drawArena(ctx, backdrop, t, 0.15);
    const g = ctx.createLinearGradient(0, 0, 0, 720);
    g.addColorStop(0, "rgba(10,6,4,0.15)");
    g.addColorStop(1, "rgba(10,6,4,0.55)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, 720);
    heroActor.draw(ctx, t, GROUND - 40, 2);
    other?.draw(ctx, t, GROUND - 40, other.look.kind === "human" ? 1.45 : 2);
    if (index >= 0 && text.textContent !== full) {
      const n = Math.floor((t - shownAt) * CPS);
      text.textContent = full.slice(0, n);
    }
  });

  const who = h("div", { class: "who" });
  const text = h("div");
  const dialog = h("div", { class: "dialog panel", onclick: advance }, who, text, h("div", { class: "next" }, "click ▸"));
  const skip = btn("Skip ▸▸", finish, "small wood skip");

  function finish(): void {
    if (done) return;
    done = true;
    onDone();
  }

  function advance(): void {
    if (index >= 0 && text.textContent !== full) {
      text.textContent = full;
      return;
    }
    index++;
    const line = lines[index];
    if (!line) return finish();
    full = line.text.replaceAll("{name}", me.name);
    shownAt = now();
    text.textContent = "";
    dialog.classList.toggle("narration", line.who === "narrator");
    who.className = "who";
    if (line.who === "narrator") {
      who.textContent = "Narrator";
      who.classList.add("narrator");
    } else if (line.who === "hero") {
      who.textContent = me.name;
      void heroActor.play("taunt", now());
      sfx.cluck();
    } else {
      const op = opponentById(line.who);
      who.textContent = op?.name ?? line.who;
      who.classList.add("enemy");
      if (op && otherId !== op.id) {
        other = new Actor(op.look, op.gear, -1, 950);
        otherId = op.id;
      }
      void other?.play("taunt", now());
      sfx.cluck();
    }
  }

  const keys = (e: KeyboardEvent) => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      if (started) advance();
      else startDialog();
    } else if (e.key === "Escape") finish();
  };

  const info = chapter === undefined ? undefined : CHAPTERS[chapter];
  const card = h(
    "div",
    { class: "chapter-card", onclick: () => startDialog() },
    info && h("div", { class: "num" }, info.title),
    info && h("div", { class: "name" }, info.subtitle),
    info && h("div", { class: "region" }, info.region),
  );

  let started = false;
  function startDialog(): void {
    if (started || done) return;
    started = true;
    card.remove();
    dialog.hidden = false;
    advance();
  }

  dialog.hidden = true;
  mount(keys, dialog, skip, muteButton(), info && card);
  if (info) {
    sfx.fanfare();
    setTimeout(startDialog, 2600);
  } else startDialog();
}
